import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import SubmitButton from "~/components/formElements/SubmitButton";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { updateInvoiceTotals } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import { getInvoiceTotalsSchema } from "~/modules/billing/utils/billingUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	isGrossBased: boolean;
	subtotalCents: number;
	totalAmountCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
}

type Props = {
	invoiceData: Invoice
	onCancel: () => void;
	onSuccess: () => void;
};

const UpdateInvoiceTotalForm = ({
	invoiceData,
	onCancel,
	onSuccess,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");
	const isReversalInvoice = invoiceData.invoiceTypeId === invoiceTypeId.ReversalInvoice;
	const schema = useMemo(() => {
		return yup.object({
			...getInvoiceTotalsSchema(isReversalInvoice),
		});
	}, [isReversalInvoice]);
	const isNonProjectInvoice = !invoiceData.isProjectInvoice;
	const defaultValues = useMemo(() => {
		return {
			isGrossBased: !isNonProjectInvoice ? false : invoiceData.isGrossBased,
			subtotalCents: invoiceData.subtotalCents,
			totalAmountCents: invoiceData.totalAmountCents,
			travelCostCentsBeforeVat: invoiceData.travelCostCentsBeforeVat,
			vatPercentage: invoiceData.vatPercentage,
		};
	}, [isNonProjectInvoice, invoiceData.isGrossBased, invoiceData.subtotalCents, invoiceData.totalAmountCents, invoiceData.travelCostCentsBeforeVat, invoiceData.vatPercentage]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		setValue,
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			await updateInvoiceTotals({ invoiceId: invoiceData.id, updateData: data });
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, onSuccess]);


	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnungsbetrag aktualisieren</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<InvoiceTotalsFormSection<FormData> control={control}
													errorMessage={errors?.totalAmountCents?.message || errors?.subtotalCents?.message}
													allowGrossInput={isNonProjectInvoice}
													includeTravelCosts={!isNonProjectInvoice}
													setValue={setValue}
													watch={watch}
													title="Rechnungsbetrag" />
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateInvoiceTotalForm;
